import React, { FC } from 'react';
import cs from 'classnames';
import { useTranslation } from '@wix/wix-i18n-config';
import { VIEW_MODE } from '../../utils/helpers';
import s from './WixStudioToolBar.scss';
import { WixStudioLogo } from '../WixStudioLogo/WixStudioLogo';
import { Switcher } from './Switcher/Switcher';
import { EditTemplateButton } from './EditTemplateButton/EditTemplateButton';
import { useTemplateContext } from '../../context/TemplateContext';
import { wixStudioToolBarDataHooks } from './WixStudioToolBar.dataHooks';
import StudioTwoLogo from './StudioTwoLogo.svg';
import DesktopIcon from './DesktopIcon.svg';
import TabletIcon from './TabletIcon.svg';
import MobileIcon from './MobileIcon.svg';

interface Props {
  dataHook?: string;
  viewMode: VIEW_MODE;
  toggleViewMode: (viewMode: VIEW_MODE) => any;
}

const DEVICE_TOGGLE_ITEMS = [
  { viewMode: VIEW_MODE.DESKTOP, Icon: DesktopIcon, tooltipKey: 'toolbar.tooltip.desktop' },
  { viewMode: VIEW_MODE.TABLET, Icon: TabletIcon, tooltipKey: 'toolbar.tooltip.tablet' },
  { viewMode: VIEW_MODE.MOBILE, Icon: MobileIcon, tooltipKey: 'toolbar.tooltip.mobile' },
];

const STUDIO2_TEMPLATE_DEVICE_TOGGLE_ITEMS = [
  { viewMode: VIEW_MODE.DESKTOP, Icon: DesktopIcon, tooltipKey: 'toolbar.tooltip.desktop' },
  { viewMode: VIEW_MODE.MOBILE, Icon: MobileIcon, tooltipKey: 'toolbar.tooltip.mobile' },
];

export const WixStudioToolBar: FC<Props> = ({ viewMode, toggleViewMode, dataHook }) => {
  const { t } = useTranslation();
  const { isStudio2Ui, isStudio2 } = useTemplateContext();
  const deviceToggleItems = isStudio2 ? STUDIO2_TEMPLATE_DEVICE_TOGGLE_ITEMS : DEVICE_TOGGLE_ITEMS;

  if (!isStudio2Ui) {
    return (
      <div data-hook={dataHook} className={s.wixStudioToolBar}>
        <div className={s.logo}>
          <WixStudioLogo color="dark" />
        </div>
        <div className={s.switcher}>
          <Switcher viewMode={viewMode} toggleViewMode={toggleViewMode} />
        </div>
        <EditTemplateButton />
      </div>
    );
  }

  return (
    <div data-hook={dataHook} className={cs(s.wixStudioToolBar, s.studio2)}>
      <a className={s.studio2Logo} href="/studio/templates" aria-label="Wix Studio">
        <StudioTwoLogo />
      </a>
      <div className={s.switcher}>
        <div className={s.deviceToggle} data-hook={wixStudioToolBarDataHooks.deviceToggle}>
          {deviceToggleItems.map(({ viewMode: itemViewMode, Icon, tooltipKey }) => (
            <button
              key={itemViewMode}
              className={cs(s.deviceToggleItem, { [s.selected]: itemViewMode === viewMode })}
              aria-label={t(tooltipKey)}
              aria-pressed={itemViewMode === viewMode}
              onClick={() => toggleViewMode(itemViewMode)}
            >
              <Icon />
            </button>
          ))}
        </div>
      </div>
      <EditTemplateButton />
    </div>
  );
};
